'use client';

import { LayoutContainer, Section } from '@petsmart-ui/sparky';
import cn from 'classnames';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import type { FC } from 'react';

import { useSitePreviewData } from '@libs/common-next';
import type { Offer } from '@libs/common-types';

import type { CardUpVariantType } from '../../types';
import { getPlpModuleSectionHeading, getTrimmedDisplayTitle } from '../../utils/plpModuleSectionHeading';
import { ColoredTextLink } from '../ColoredTextLink';
import { trackCardUpLinkActionClick } from './analytics';
import CardUpVariant from './CardUpVariant';
import { OfferPattern } from './offer-pattern';
import type { Card, CardUpBrandProps, CardUpOfferProps, CardUpProps } from './types';
import { prepareEligibleOffers } from './utils';

import './CardInfo.scss';
import './CardUp.scss';
import './CardUpArticle.scss';

const CardUpArticleVariant = dynamic(() => import('./CardUpArticleVariant'), {
    ssr: false,
});
const CardUpBrandVariant = dynamic(() => import('./CardUpBrandVariant'), {
    ssr: false,
});
const CardUpOfferVariant = dynamic(() => import('./card-up-offer-variant'));

const ComponentCardUp: Record<CardUpVariantType, FC<CardUpProps>> = {
    '2up': props => <CardUpVariant sliceMax={2} {...props} />,
    '2up Brand': props => <CardUpBrandVariant sliceMax={2} {...(props as unknown as CardUpBrandProps)} />,
    '3up': props => <CardUpVariant sliceMax={3} {...props} />,
    '4up': props => <CardUpVariant sliceMax={4} {...props} />,
    '4Stack': props => <CardUpVariant sliceMax={4} {...props} />,
    '3up Article': props => <CardUpArticleVariant articlesPerView={3} {...props} />,
    Info: props => <CardUpVariant sliceMax={4} {...props} />,
    Offer: props => <CardUpOfferVariant {...(props as unknown as CardUpOfferProps)} />,
};

const validateCardTypes = (cards: Card[]): boolean => {
    const articleModel = cards.some(card => card.previewDescription);
    const articleModelsAll = cards.every(card => card.previewDescription);
    return articleModel && !articleModelsAll;
};

const getEligibleCards = (variant: CardUpVariantType, cards: Card[], previewDate?: string): Card[] | Offer[] => {
    if (variant === 'Offer') {
        return prepareEligibleOffers(cards ?? [], previewDate ? new Date(previewDate) : new Date());
    }

    return cards;
};

export default function CardUp(props: CardUpProps) {
    if (props.variant === 'Offer') {
        return (
            <OfferPattern>
                <CardUpSection {...props} />
            </OfferPattern>
        );
    }

    return <CardUpSection {...props} />;
}

function CardUpSection({
    header,
    description,
    linkAction,
    linkActionColor,
    variant,
    cards,
    backgroundColor,
    isH1,
}: CardUpProps) {
    const pathname = usePathname();
    const previewDate = useSitePreviewData()?.previewDate;
    const cardsToRender = getEligibleCards(variant, cards, previewDate);

    if (!cardsToRender?.length) {
        return null;
    }

    const Component = ComponentCardUp[variant];

    if (!Component) {
        return null;
    }

    if (variant === '3up Article' && validateCardTypes(cards)) {
        return null;
    }

    const { link, actionText } = linkAction || {};
    const displayHeader = getTrimmedDisplayTitle(header);
    const { title: sectionTitle, headingTagName } = getPlpModuleSectionHeading(header, isH1);

    const titleAfter =
        displayHeader && link && actionText ? (
            <ColoredTextLink
                data-testid="link-action"
                onClick={() =>
                    trackCardUpLinkActionClick({
                        header,
                        linkAction,
                        offerCount: cardsToRender.length,
                        pathname: pathname ?? '',
                        variant,
                    })
                }
                href={link?.webUrl}
                color={linkActionColor}
            >
                {actionText}
            </ColoredTextLink>
        ) : null;

    const variantName = variant.toLowerCase().replaceAll(' ', '-');

    const noHeaderClass = !displayHeader ? `${variantName}--no-header` : '';

    const restyledVariants = ['2up', '3up', '3up Article', '4up', 'Info', '4Stack', 'Offer'];

    const isRestyled = restyledVariants.includes(variant);

    const componentClassName = cn('card-up', variantName, noHeaderClass, isRestyled && 'card-up--restyled');
    const cardHeaderTagName = isH1 || !displayHeader ? 'h2' : 'h3';

    return (
        <LayoutContainer className={`card-up-container card-up-container--${variantName}`}>
            <Section
                data-testid="card-up"
                data-variant={variantName}
                className={componentClassName}
                title={sectionTitle}
                description={description}
                titleAfter={titleAfter}
                style={{ backgroundColor }}
                headingTagName={headingTagName}
                {...(isRestyled && {
                    align: 'center',
                    titleAfterLocation: 'below-description',
                })}
            >
                <Component
                    header={header}
                    description={description}
                    linkAction={linkAction}
                    cards={cardsToRender as Card[]}
                    variant={variant}
                    pathname={pathname ?? ''}
                    isH1={isH1}
                    cardHeaderTagName={cardHeaderTagName}
                />
            </Section>
        </LayoutContainer>
    );
}
